import {
  BoolListContains,
  Not,
  NumberEquals,
  NumberGreaterThan,
  StringContainsFold,
  StringEquals,
  StringListContains,
  StringListContainsAll,
  TimeAfter,
  TimeBefore,
} from '@vendasta/automata';
import { BoolListOptionsComponent } from './dynamic-options/bool-list-options/bool-list-options.component';
import { CurrencyInputDisplayService } from './dynamic-options/currency-input/currency-input-display.service';
import { CurrencyInputComponent } from './dynamic-options/currency-input/currency-input.component';
import { DateInputDisplayService } from './dynamic-options/date-input/date-input-display.service';
import { DateInputComponent } from './dynamic-options/date-input/date-input.component';
import { EmptyStringDisplayService } from './dynamic-options/empty-string-component';
import { FreeTextDisplayService } from './dynamic-options/free-text-input/free-text-display.service';
import { FreeTextInputComponent } from './dynamic-options/free-text-input/free-text-input.component';
import { MarketOptionsDisplayService } from './dynamic-options/market-options/market-options-display.service';
import { MarketOptionsComponent } from './dynamic-options/market-options/market-options.component';
import { OrderOriginOptionsComponent } from './dynamic-options/order-origin-options/order-origin-options-component';
import { OrderOriginOptionsDisplayService } from './dynamic-options/order-origin-options/order-origin-options-display.service';
import { OrderStatusOptionsComponent } from './dynamic-options/order-status-options/order-status-options-component';
import { OrderStatusOptionsDisplayService } from './dynamic-options/order-status-options/order-status-options-display.service';
import { OrderTagOptionsComponent } from './dynamic-options/order-tag-options/order-tag-options.component';
import { PositiveIntegerDisplayService } from './dynamic-options/positive-integer/positive-integer-display.service';
import { PositiveIntegerComponent } from './dynamic-options/positive-integer/positive-integer.component';
import { TagOptionsDisplayService } from './dynamic-options/tag-options/tag-options-display.service';
import { DataFilter } from './interface';

const ORDER_STATUS = '{.order_details.status}';
class OrderStatusDataFilter implements DataFilter {
  rules = [new NumberEquals(ORDER_STATUS), new Not(new NumberEquals(ORDER_STATUS))];
  dataEntryComponentList = [OrderStatusOptionsComponent, OrderStatusOptionsComponent];
  displayService = OrderStatusOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.STATUS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.IS',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.IS_NOT',
  ];
}

const CREATED_DATE = '{.order_details.created}';
class CreatedDateDataFilter implements DataFilter {
  rules = [new TimeAfter(CREATED_DATE), new TimeBefore(CREATED_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.CREATED_DATE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.AFTER',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.BEFORE',
  ];
}

const REQUESTED_ACTIVATION_DATE = '{.order_details.requested_activation}';
class RequestedActivationDateDataFilter implements DataFilter {
  rules = [new TimeAfter(REQUESTED_ACTIVATION_DATE), new TimeBefore(REQUESTED_ACTIVATION_DATE)];
  dataEntryComponent = DateInputComponent;
  displayService = DateInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.REQUESTED_ACTIVATION_DATE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.AFTER',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.BEFORE',
  ];
}

const TAGS = '{.order_details.tags}';
class TagDataFilter implements DataFilter {
  rules = [
    new StringListContains(TAGS),
    new StringListContainsAll(TAGS),
    new Not(new StringListContains(TAGS)),
    new Not(new StringListContainsAll(TAGS)),
  ];
  dataEntryComponent = OrderTagOptionsComponent;
  displayService = TagOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.TAGS';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.INCLUDES_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.INCLUDES_ALL',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.DOES_NOT_INCLUDE_ANY',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.DOES_NOT_INCLUDE_ALL',
  ];
}

const ATTACHMENT_COUNT = '{.computed_properties.attachment_count}';
class AttachmentCountDataFilter implements DataFilter {
  rules = [
    new Not(new NumberGreaterThan(ATTACHMENT_COUNT)),
    new NumberEquals(ATTACHMENT_COUNT),
    new NumberGreaterThan(ATTACHMENT_COUNT),
  ];
  dataEntryComponentList = [PositiveIntegerComponent, PositiveIntegerComponent, PositiveIntegerComponent];
  displayService = PositiveIntegerDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.ATTACHMENT_COUNT';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const ORDER_ORIGIN = '{.order_details.origin}';
class OrderOriginDataFilter implements DataFilter {
  rules = [new StringEquals(ORDER_ORIGIN), new Not(new StringEquals(ORDER_ORIGIN))];
  dataEntryComponentList = [OrderOriginOptionsComponent, OrderOriginOptionsComponent];
  displayService = OrderOriginOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.ORIGIN';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.IS',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.IS_NOT',
  ];
}

const NOTES = '{.order_details.notes}';
class NotesDataFilter implements DataFilter {
  rules = [new StringContainsFold(NOTES), new Not(new StringContainsFold(NOTES))];
  dataEntryComponent = FreeTextInputComponent;
  displayService = FreeTextDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.NOTES';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.CONTAINS',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.DOES_NOT_CONTAIN',
  ];
}

const ANNUAL_CONTRACT_VALUE = '{.computed_properties.annual_contract_value}';
class AnnualContractValueDataFilter implements DataFilter {
  rules = [new Not(new NumberGreaterThan(ANNUAL_CONTRACT_VALUE)), new NumberGreaterThan(ANNUAL_CONTRACT_VALUE)];
  dataEntryComponentList = [CurrencyInputComponent, CurrencyInputComponent];
  displayService = CurrencyInputDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.ANNUAL_CONTRACT_VALUE';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.LESS_THAN_EQUAL',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.GREATER_THAN',
  ];
}

const ORDER_FORM_ENABLED = '{.order_details.products[*].activation_information.order_form_enabled}';
class OrderFormEnabledDataFilter implements DataFilter {
  label = 'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.DATA_LABELS.ORDER_FORM_ENABLED_FOR_PRODUCTS';
  dataEntryComponent = BoolListOptionsComponent;
  displayService = EmptyStringDisplayService;
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.ON_FOR_NONE',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.ON_FOR_SOME',
    'AUTOMATIONS.EDITOR.FILTERS.ORDER_DATA.COMPARISON.ON_FOR_ALL',
  ];
  rules = [
    new Not(new BoolListContains(ORDER_FORM_ENABLED)),
    new BoolListContains(ORDER_FORM_ENABLED),
    // Does not contain any not enabled (i.e., contains all order form enabled)
    new Not(new BoolListContains(ORDER_FORM_ENABLED, false)),
  ];
}

const MARKETS = '{.order_details.market_id}';
class MarketDataFilter implements DataFilter {
  rules = [new StringEquals(MARKETS), new Not(new StringEquals(MARKETS))];
  dataEntryComponent = MarketOptionsComponent;
  displayService = MarketOptionsDisplayService;
  label = 'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.MARKET';
  comparisonLabels = [
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.EQUALS',
    'AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.COMPARISON.NOT_EQUALS',
  ];
}

export const ORDER_DATA_COMPONENTS: Map<string, DataFilter> = new Map<string, DataFilter>([
  [ORDER_STATUS, new OrderStatusDataFilter()],
  [ANNUAL_CONTRACT_VALUE, new AnnualContractValueDataFilter()],
  [ORDER_FORM_ENABLED, new OrderFormEnabledDataFilter()],
  [CREATED_DATE, new CreatedDateDataFilter()],
  [REQUESTED_ACTIVATION_DATE, new RequestedActivationDateDataFilter()],
  [TAGS, new TagDataFilter()],
  [ATTACHMENT_COUNT, new AttachmentCountDataFilter()],
  [ORDER_ORIGIN, new OrderOriginDataFilter()],
  [NOTES, new NotesDataFilter()],
  [MARKETS, new MarketDataFilter()],
]);
